import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { RecipeCard } from '../components/RecipeCard';
import { SEO } from '../components/SEO';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, Leaf } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Recipes = () => {
  const { t, getLocalizedText, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const categoryParam = searchParams.get('category');
  const courseParam = searchParams.get('course');
  const [selectedCategory, setSelectedCategory] = useState(categoryParam || 'all');
  const [selectedCourse, setSelectedCourse] = useState(courseParam || 'all');

  const categories = [
    { value: 'all', label: t('recipes.allCategories') },
    { value: 'express', label: t('recipes.express') },
    { value: 'guests', label: t('recipes.guests') },
    { value: 'casual', label: t('recipes.casual') },
  ];

  const courseTypes = [
    { value: 'all', label: t('recipes.allCourses') },
    { value: 'appetizer', label: t('recipes.appetizer') },
    { value: 'main', label: t('recipes.main') },
    { value: 'dessert', label: t('recipes.dessert') },
    { value: 'snack', label: t('recipes.snack') },
  ];

  useEffect(() => {
    fetchRecipes();
  }, [selectedCategory]);

  useEffect(() => {
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
    if (courseParam) {
      setSelectedCourse(courseParam);
    }
  }, [categoryParam, courseParam]);

  const fetchRecipes = async () => {
    setLoading(true);
    try {
      const url = selectedCategory === 'all' 
        ? `${API}/recipes`
        : `${API}/recipes?category=${selectedCategory}`;
      const response = await axios.get(url);
      setRecipes(response.data);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const handleCourseChange = (course) => {
    setSelectedCourse(course);
    if (course === 'all') {
      searchParams.delete('course');
    } else {
      searchParams.set('course', course);
    }
    setSearchParams(searchParams);
  };

  const filteredRecipes = recipes.filter((recipe) => {
    // Filter by course type
    if (selectedCourse !== 'all' && recipe.course_type !== selectedCourse) {
      return false;
    }
    // Filter by search query
    if (!searchQuery) return true;
    const title = getLocalizedText(recipe.title).toLowerCase();
    const description = getLocalizedText(recipe.description).toLowerCase();
    return title.includes(searchQuery.toLowerCase()) || description.includes(searchQuery.toLowerCase());
  });

  const getSEOContent = () => {
    const content = {
      en: {
        title: 'Vegan Recipes - Delicious Plant-Based Meals | love4vegan',
        description: 'Browse our collection of delicious vegan recipes. From quick meals to gourmet dishes, find your perfect plant-based recipe today!'
      },
      fi: {
        title: 'Vegaanireseptit - Herkulliset Kasvisruoat | love4vegan',
        description: 'Selaa herkullisia vegaanireseptejämme. Nopeista aterioista gourmet-annoksiin, löydä täydellinen kasvisreseptisi tänään!'
      },
      fr: {
        title: 'Recettes Véganes - Plats Végétaliens Délicieux | love4vegan',
        description: 'Parcourez notre collection de recettes véganes. Des repas rapides aux plats gastronomiques, trouvez votre recette parfaite!'
      }
    };
    return content[language] || content.en;
  };

  const seoContent = getSEOContent();

  return (
    <div className="min-h-screen py-12" data-testid="recipes-page">
      <SEO 
        title={seoContent.title}
        description={seoContent.description}
        image="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1200"
        url="https://love4vegan.com/recipes"
        type="website"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-4">
            {t('recipes.title')}
          </h1>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col gap-4 mb-12">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-foreground/40" />
            <Input
              placeholder={t('recipes.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-6 rounded-full border-border bg-card"
              data-testid="recipe-search-input"
            />
          </div>

          {/* Filters Row */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-foreground/50 self-center mr-2">{t('admin.recipeCategory')}:</span>
              {categories.map((category) => (
                <Button
                  key={category.value}
                  variant={selectedCategory === category.value ? 'default' : 'outline'}
                  size="sm"
                  className={`rounded-full ${
                    selectedCategory === category.value
                      ? 'bg-secondary text-secondary-foreground'
                      : 'border-border hover:bg-muted/20'
                  }`}
                  onClick={() => handleCategoryChange(category.value)}
                  data-testid={`category-filter-${category.value}`}
                >
                  {category.label}
                </Button>
              ))}
            </div>

            {/* Course Type Filters */}
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-foreground/50 self-center mr-2">{t('recipes.courseType')}:</span>
              {courseTypes.map((course) => (
                <Button
                  key={course.value}
                  variant={selectedCourse === course.value ? 'default' : 'outline'}
                  size="sm"
                  className={`rounded-full ${
                    selectedCourse === course.value
                      ? 'bg-primary text-primary-foreground'
                      : 'border-border hover:bg-muted/20'
                  }`}
                  onClick={() => handleCourseChange(course.value)}
                  data-testid={`course-filter-${course.value}`}
                >
                  {course.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Recipe Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-card rounded-2xl h-80 animate-pulse" />
            ))}
          </div>
        ) : filteredRecipes.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRecipes.map((recipe) => (
              <RecipeCard key={recipe.id} recipe={recipe} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Leaf className="h-16 w-16 text-muted mx-auto mb-4" />
            <p className="text-foreground/60 text-lg">
              {t('recipes.noRecipes')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
